import React from 'react';
import { Chip, Tooltip, makeStyles } from '@material-ui/core';
import moment from 'moment';
import { ISubstitutionMark } from 'interfaces';

const useStyles = makeStyles({
  chip: {
    height: 20,
    fontSize: 11,
  },
  // Цвета заменяющего повторяют чип замещения в матрице должностей.
  substitute: {
    backgroundColor: '#FFF3CD',
    borderColor: '#FFE69C',
    color: '#856404',
  },
  substituted: {
    backgroundColor: '#EEEEEE',
    borderColor: '#E0E0E0',
    color: '#616161',
  },
});

interface IProps {
  substitution?: ISubstitutionMark | null;
  className?: string;
}

const formatDate = (date?: string | null): string => (date ? moment(date).format('DD.MM.YYYY') : '');

const fullName = (lastName?: string | null, firstName?: string | null): string =>
  `${lastName ?? ''} ${firstName ?? ''}`.trim();

const period = (substitution: ISubstitutionMark): string => {
  const from = formatDate(substitution.substitutionStartDate);
  const to = formatDate(substitution.substitutionEndDate);

  if (from && to) return `С ${from} по ${to}`;
  if (from) return `С ${from}`;
  return '';
};

/**
 * Метка замещения рядом с ФИО сотрудника: «Заменяющий» — временно исполняет чужую должность,
 * «Замещён» — его самого сейчас замещают. Без пометки ничего не рисует.
 */
const SubstitutionChip: React.FC<IProps> = ({ substitution, className }) => {
  const classes = useStyles();

  if (!substitution) return null;

  const isSubstitute = Boolean(substitution.isSubstitute);
  const isSubstituted = Boolean(substitution.isSubstituted);

  if (!isSubstitute && !isSubstituted) return null;

  const counterpart = isSubstitute
    ? fullName(substitution.originalExecutorLastName, substitution.originalExecutorFirstName)
    : fullName(substitution.substituteExecutorLastName, substitution.substituteExecutorFirstName);

  const label = isSubstitute ? 'Заменяющий' : 'Замещён';
  const title = [
    isSubstitute ? `Замещает: ${counterpart}` : `Замещает его: ${counterpart}`,
    period(substitution),
    substitution.substitutionReason ? `Причина: ${substitution.substitutionReason}` : '',
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{title}</span>}>
      <Chip
        size="small"
        variant="outlined"
        label={label}
        className={`${classes.chip} ${isSubstitute ? classes.substitute : classes.substituted} ${className ?? ''}`}
      />
    </Tooltip>
  );
};

export default SubstitutionChip;
